import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  conversationQuery,
  useDismissAiDraft,
  useSendAiDraft,
  useSetConversationMode,
} from '@/entities/conversation'
import { draftRefusal, refusedByServer } from './draft-refusal'

/** Какой черновик и в какой переписке мы отправили — сервер ответил `201`. */
type Sent = { conversationId: string; draftId: string }

/**
 * Отказ «Отправить». `seen` — черновик, который мы увидели после отказа: в перечитанной шапке, а если
 * перечитать не вышло — в кеше; `undefined` — ещё перечитываем.
 */
type Failure = { conversationId: string; draftId: string; error: unknown; seen?: string | null }

export type AiDraftReview = ReturnType<typeof useAiDraftReview>

/**
 * Черновик в шапке, перечитанной после отказа. На отказ сервера шапку перезапрашивает сама мутация, и
 * `fetchQuery` дождётся того же запроса; на обрыве сети — свой запрос или летящий опрос. Не вышло —
 * то, что в кеше. Без сети запрос ждёт её появления.
 */
async function draftAfterRefusal(queryClient: QueryClient, conversationId: string) {
  const query = conversationQuery(conversationId)
  try {
    return (await queryClient.fetchQuery({ ...query, retry: false })).aiDraft?.id ?? null
  } catch {
    return queryClient.getQueryData(query.queryKey)?.aiDraft?.id ?? null
  }
}

/**
 * Состояние карточки черновика консьержа — для ОДНОЙ переписки.
 *
 * ⚠️ ОБА ДЕЙСТВИЯ НАЗЫВАЮТ ЧЕРНОВИК НА ЭКРАНЕ (`draft.id`). Между последним чтением шапки и кликом
 * ИИ может заменить черновик более новым, и «отправь текущий» ушёл бы к гостю текстом, которого
 * менеджер не читал. Черновик, который больше не ждёт решения, сервер не отправит.
 *
 * ⚠️ ОТМЕТКА «ОТПРАВЛЕН» И ОТКАЗ — СВОЁ СОСТОЯНИЕ, А НЕ `send.isSuccess`/`send.error`: оба помнят
 * переписку и черновик, поэтому у соседней переписки их нет, а новый черновик гасит отметку —
 * «отправлен» под следующим вопросом гостя говорило бы о черновике, которого менеджер не отправлял.
 *
 * ⚠️ ОТКАЗ ГОВОРИТСЯ СРАЗУ, А УТОЧНЯЕТСЯ ПОТОМ: общий тост решил молчать в момент отказа, и ждать
 * перечитанной шапки молча нельзя — уйди менеджер в это окно, отказ не сказал бы никто. Уточнённый,
 * он живёт, пока черновик на экране тот же: следующая смена — уже другая история. Исключение —
 * неизвестный исход: исчезновение черновика вопрос «дошёл ли» не закрывает, и строка остаётся до
 * нового черновика. Снимает отказ и любое действие.
 *
 * ⚠️ ФИЧА ОБЯЗАНА БЫТЬ ВЕРНОЙ И БЕЗ КЛЮЧА НА РОДИТЕЛЕ: смена переписки отпускает летящие запросы
 * (эффект ниже). Отказ отпущенной отправки скажет общий тост с предметом (`meta.subject`), и карточка
 * о нём не говорит — ни соседняя, ни эта же, когда к ней вернутся; отказы «Отклонить» и «Править»
 * тост говорит всегда. Отказ, пришедший до смены, к тому времени уже сказан.
 */
export function useAiDraftReview(conversationId: string) {
  const queryClient = useQueryClient()
  const { data: conversation } = useQuery(conversationQuery(conversationId))
  const draft = conversation?.aiDraft ?? null
  const shown = draft?.id ?? null

  const send = useSendAiDraft(
    conversationId,
    `Отправка черновика ИИ · ${conversation?.guestName ?? 'переписка'}`,
  )
  const dismiss = useDismissAiDraft(conversationId)
  const mode = useSetConversationMode(conversationId)

  const [sent, setSent] = useState<Sent | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  // Каждое решение по черновику — новая попытка: исход прежней больше не наш.
  const attempt = useRef(0)
  // Черновик, чей текст уже отдан в поле этой переписки (`edit`).
  const handed = useRef<string | null>(null)

  // Правки состояния прямо в рендере — штатный приём React: рендер перезапускается до покраски, и
  // кадра с устаревшей отметкой или отказом не будет.
  if (sent?.conversationId === conversationId && draft && draft.id !== sent.draftId) setSent(null)
  if (
    failure?.conversationId === conversationId &&
    failure.seen !== undefined &&
    shown !== failure.seen &&
    !(shown === null && !refusedByServer(failure.error))
  ) {
    setFailure(null)
  }

  const { reset: releaseSend } = send
  const { reset: releaseDismiss } = dismiss
  const { reset: releaseMode } = mode
  useEffect(
    () => () => {
      attempt.current += 1
      handed.current = null
      releaseSend()
      releaseDismiss()
      releaseMode()
    },
    [conversationId, releaseSend, releaseDismiss, releaseMode],
  )

  const submit = (): void => {
    if (!draft) return
    const target = { conversationId, draftId: draft.id }
    const mine = (attempt.current += 1)
    setFailure(null)

    void (async () => {
      try {
        await send.mutateAsync(target.draftId)
        // ⚠️ Отметка честна ровно здесь: `mutateAsync` резолвится только `201`-м.
        if (attempt.current === mine) setSent(target)
      } catch (error) {
        // ⚠️ ЛОВИМ ОБЯЗАТЕЛЬНО: без `catch` unhandled rejection уводит приложение на экран смерти.
        if (attempt.current !== mine) return
        const provisional: Failure = { ...target, error }
        setFailure(provisional)
        const seen = await draftAfterRefusal(queryClient, target.conversationId)
        // Уточняем, только если отказ всё тот же: решение, принятое за это время, его уже сняло.
        setFailure((current) => (current === provisional ? { ...provisional, seen } : current))
      }
    })()
  }

  /** Другое решение по черновику снимает отказ отправки: говорить о нём больше незачем. */
  const decide = (action: () => void) => () => {
    attempt.current += 1
    setFailure(null)
    action()
  }
  const takeOver = decide(() => mode.mutate('manager'))

  return {
    /** Висящий черновик этой переписки, `null` — черновика нет. */
    draft,
    /** Черновика нет, и последний увиденный отправили мы: сервер ответил `201`. */
    sent: !draft && sent?.conversationId === conversationId,
    /** Что сказать об отказе «Отправить» на месте карточки (`model/draft-refusal.ts`). */
    refusal:
      failure?.conversationId === conversationId
        ? draftRefusal(failure.error, failure.draftId, failure.seen === undefined ? undefined : draft)
        : null,
    // Пока летит любое из трёх действий, кнопки заблокированы: три конкурирующих запроса про один
    // черновик человеку не нужны.
    busy: send.isPending || dismiss.isPending || mode.isPending,
    /** «Отправить» — черновик на экране, как есть. */
    send: submit,
    /** «Отклонить» — черновик на экране. Наружу ничего не уходит. */
    dismiss: decide(() => {
      if (draft) dismiss.mutate(draft.id)
    }),
    /**
     * «Править» → текст черновика в поле и режим `manager` (сервер при этом сам отклоняет черновик).
     * Текст — раз на черновик: повтор упавшей смены режима отдаёт пустой, то есть только фокус.
     */
    edit: (put: (text: string) => void) => {
      if (!draft) return
      put(handed.current === draft.id ? '' : draft.text)
      handed.current = draft.id
      takeOver()
    },
  }
}
